import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFavorites } from "@/lib/favorites";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/drama" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { favorites } = useFavorites();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || menuOpen;

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/about") return location.pathname === "/about";
    return location.pathname === to;
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/80" : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[68px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span
            className={`w-9 h-9 rounded-md flex items-center justify-center ${
              solid ? "bg-primary-600 text-background-50" : "bg-background-50/15 text-background-50 backdrop-blur-sm"
            }`}
          >
            <i className="ri-movie-2-fill text-lg w-5 h-5 flex items-center justify-center" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-base md:text-lg font-black tracking-wide ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              日韩在线
            </span>
            <span
              className={`font-label text-[10px] tracking-[0.18em] uppercase mt-1 ${
                solid ? "text-foreground-500" : "text-background-50/70"
              }`}
            >
              JK Online
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((nav) => (
            <Link
              key={nav.to}
              to={nav.to}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                isActive(nav.to)
                  ? solid
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-50/20 text-background-50"
                  : solid
                    ? "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                    : "text-background-50/85 hover:bg-background-50/15 hover:text-background-50"
              }`}
            >
              {nav.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to="/search"
            className={`hidden sm:flex items-center gap-2 px-3.5 h-9 rounded-full text-sm whitespace-nowrap transition-colors ${
              solid
                ? "bg-background-100 text-foreground-600 hover:bg-background-200"
                : "bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/25"
            }`}
          >
            <i className="ri-search-line text-base w-4 h-4 flex items-center justify-center" />
            <span className="font-label text-xs">搜索影视</span>
          </Link>
          <Link
            to="/favorites"
            className={`relative flex items-center gap-1.5 px-3 h-9 rounded-full text-sm whitespace-nowrap transition-colors ${
              solid
                ? "bg-background-100 text-foreground-700 hover:bg-background-200"
                : "bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/25"
            }`}
            aria-label="我的收藏"
          >
            <i className="ri-heart-3-line text-base w-4 h-4 flex items-center justify-center" />
            <span className={`font-label text-xs ${favorites.length > 0 ? "text-accent-400" : ""}`}>
              {favorites.length}
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className={`md:hidden w-9 h-9 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
              solid ? "bg-background-100 text-foreground-900" : "bg-background-50/15 text-background-50"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg w-5 h-5 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden border-t border-background-200/70 bg-background-50 transition-all duration-300 ${
          menuOpen ? "max-h-[420px]" : "max-h-0 border-t-0"
        }`}
      >
        <nav className="px-4 py-3 flex flex-col gap-1">
          {navItems.map((nav) => (
            <Link
              key={nav.to}
              to={nav.to}
              className={`px-3 py-3 rounded-md text-sm font-medium flex items-center justify-between ${
                isActive(nav.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {nav.label}
              <i className="ri-arrow-right-s-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
            </Link>
          ))}
          <Link
            to="/search"
            className="px-3 py-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 flex items-center justify-between"
          >
            搜索影视
            <i className="ri-search-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
          </Link>
          <Link
            to="/favorites"
            className="px-3 py-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 flex items-center justify-between"
          >
            我的收藏（{favorites.length}）
            <i className="ri-heart-3-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
          </Link>
        </nav>
      </div>
    </header>
  );
}